/**
 * InteligenciaNegocio - Seção de inteligência do negócio na aba Assistente
 * Cruza dados já existentes: boletim de fornecedores (prometido × realizado)
 * e desvio de orçamento por projeto (ficha técnica planejado × comprado)
 */
import React, { useState, useEffect } from 'react';
import api from '../../lib/axios';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Loader2, Truck, FolderKanban, TrendingUp, TrendingDown, Clock, MessageSquare, PackageX } from 'lucide-react';

const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);

/* ---------- Card de fornecedor (boletim) ---------- */
const FornecedorCard = ({ f }) => {
  const atraso = f.atraso_medio_dias;
  return (
    <div className="p-3 rounded-lg border border-slate-200 bg-white" data-testid={`intel-fornecedor-${f.fornecedor_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-semibold text-slate-900 truncate">{f.nome || 'Fornecedor'}</p>
        <span className="text-[11px] text-slate-500 whitespace-nowrap">
          {f.pedidos_12m} pedido{f.pedidos_12m !== 1 ? 's' : ''} · {fmtBRL(f.valor_12m)} em 12m
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-2">
        {atraso != null ? (
          <Badge className={`text-[10px] gap-1 ${atraso > 2 ? 'bg-red-100 text-red-700 hover:bg-red-100' : atraso < -1 ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-100'}`}>
            <Clock className="w-3 h-3" />
            {atraso > 2 ? `atrasa ~${Math.round(atraso)}d sobre o prometido` : atraso < -1 ? `entrega ~${Math.abs(Math.round(atraso))}d antes` : 'entrega no prazo'}
          </Badge>
        ) : f.prazo_real_medio_dias != null ? (
          <Badge className="text-[10px] gap-1 bg-slate-100 text-slate-600 hover:bg-slate-100">
            <Clock className="w-3 h-3" /> entrega em ~{Math.round(f.prazo_real_medio_dias)}d
          </Badge>
        ) : null}
        {f.tempo_resposta_medio_dias != null && (
          <Badge className={`text-[10px] gap-1 ${f.tempo_resposta_medio_dias <= 2 ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-100 text-amber-700 hover:bg-amber-100'}`}>
            <MessageSquare className="w-3 h-3" /> responde em ~{Math.round(f.tempo_resposta_medio_dias)}d
          </Badge>
        )}
        {f.tendencia_preco_pct != null && Math.abs(f.tendencia_preco_pct) >= 1 && (
          <Badge className={`text-[10px] gap-1 ${f.tendencia_preco_pct > 0 ? 'bg-red-100 text-red-700 hover:bg-red-100' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'}`}>
            {f.tendencia_preco_pct > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            preços {f.tendencia_preco_pct > 0 ? '+' : ''}{f.tendencia_preco_pct}%
          </Badge>
        )}
        {f.taxa_indisponibilidade_pct != null && f.taxa_indisponibilidade_pct >= 15 && (
          <Badge className="text-[10px] gap-1 bg-orange-100 text-orange-700 hover:bg-orange-100">
            <PackageX className="w-3 h-3" /> {f.taxa_indisponibilidade_pct}% itens indisponíveis
          </Badge>
        )}
      </div>
    </div>
  );
};

/* ---------- Card de projeto (orçado × gasto) ---------- */
const ProjetoCard = ({ p }) => {
  const desvio = p.desvio_pct;
  const pctBarra = p.planejado_dos_comprados > 0
    ? Math.min((p.comprado_total / p.planejado_dos_comprados) * 100, 150)
    : 0;
  return (
    <div className="p-3 rounded-lg border border-slate-200 bg-white" data-testid={`intel-projeto-${p.projeto_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-semibold text-slate-900 truncate">{p.nome}</p>
        <span className="text-[11px] text-slate-500 whitespace-nowrap">
          {p.pct_itens_comprados}% dos itens comprados
        </span>
      </div>
      <div className="mt-2 text-[11px] text-slate-600 flex items-center justify-between flex-wrap gap-1">
        {p.itens_comprados > 0 ? (
          <>
            <span>Planejado (itens comprados): <strong>{fmtBRL(p.planejado_dos_comprados)}</strong></span>
            <span>Pago: <strong>{fmtBRL(p.comprado_total)}</strong></span>
          </>
        ) : (
          <span>Planejado total: <strong>{fmtBRL(p.planejado_total)}</strong> · nenhuma compra vinculada ainda</span>
        )}
      </div>
      {/* Barra orçado × gasto */}
      <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${desvio != null && desvio > 2 ? 'bg-red-500' : desvio != null && desvio < -2 ? 'bg-emerald-500' : 'bg-sky-500'}`}
          style={{ width: `${Math.min(pctBarra, 100)}%` }}
        />
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-2">
        {desvio != null && (
          <Badge className={`text-[10px] ${desvio > 2 ? 'bg-red-100 text-red-700 hover:bg-red-100' : desvio < -2 ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-100'}`} data-testid={`intel-desvio-${p.projeto_id}`}>
            {desvio > 0 ? '▲' : desvio < 0 ? '▼' : '='} {Math.abs(desvio)}% {desvio > 2 ? 'acima do planejado' : desvio < -2 ? 'abaixo do planejado' : 'no planejado'}
          </Badge>
        )}
        {p.extras_valor > 0 && (
          <Badge className="text-[10px] bg-amber-100 text-amber-700 hover:bg-amber-100">
            extras: {fmtBRL(p.extras_valor)}
          </Badge>
        )}
      </div>
    </div>
  );
};

const InteligenciaNegocio = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/compras/assistente/inteligencia')
      .then((r) => setData(r.data))
      .catch((err) => console.warn('Inteligência do negócio indisponível:', err?.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!data) return null;

  const fornecedores = (data.fornecedores || []).slice(0, 8);
  const projetos = (data.projetos || []).slice(0, 8);
  if (fornecedores.length === 0 && projetos.length === 0) return null;

  return (
    <div className="mt-2" data-testid="inteligencia-negocio">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-[0.15em]">Inteligência do Negócio</h3>
        <span className="text-[10px] text-slate-400">últimos 12 meses · calculado dos seus dados reais</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fornecedores */}
        <Card className="border-slate-200" data-testid="intel-fornecedores-card">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="p-1.5 rounded-lg bg-sky-100 text-sky-700"><Truck className="w-4 h-4" /></span>
              <h4 className="text-sm font-semibold text-slate-900">Boletim de Fornecedores</h4>
            </div>
            {fornecedores.length === 0 ? (
              <p className="text-xs text-slate-500 py-3 text-center">Sem histórico suficiente ainda.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {fornecedores.map((f) => <FornecedorCard key={f.fornecedor_id} f={f} />)}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Projetos */}
        <Card className="border-slate-200" data-testid="intel-projetos-card">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="p-1.5 rounded-lg bg-violet-100 text-violet-700"><FolderKanban className="w-4 h-4" /></span>
              <h4 className="text-sm font-semibold text-slate-900">Orçamento dos Projetos</h4>
            </div>
            {projetos.length === 0 ? (
              <p className="text-xs text-slate-500 py-3 text-center">Nenhum projeto com custos de compras vinculados ainda.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {projetos.map((p) => <ProjetoCard key={p.projeto_id} p={p} />)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InteligenciaNegocio;
